import { UserCell } from "@/components/ui/layout/table/userCell"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import ChatEmpty from "@images/empty/chat_empty.svg"
import { word } from "@modules/core/utils/i18n"
import { usePersistedBoxArchivedMembers } from "@modules/members/boxTerminatedMembers/hooks/useBoxArchivedMembers"
import { MemberItemEntity } from "@modules/members/entities/memberEntity"
import { MemberColumnToSort } from "@modules/members/gateway/memberDto"
import { useMemberSideBar } from "@modules/members/hooks/useMemberSidebar"
import { ColumnSortingOrder } from "@modules/types/datatableTypes"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { HeaderTemplate } from "@modules/ui/components/dataTables/templates/headerTemplate"
import { EmptyView } from "@modules/ui/components/emptyView"
import { createColumnHelper } from "@tanstack/react-table"
import dayjs from "dayjs"
import { useState } from "react"

type BoxArchivedMembersTableProps = {
  className?: string
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<MemberItemEntity>()

export function BoxArchivedMembersTable({ className, ...props }: BoxArchivedMembersTableProps) {
  const [selectedArchivedMember, setSelectedArchivedMember] = useState<MemberItemEntity>()
  const { navigateToMemberDetailsInfo } = useMemberSideBar()

  const {
    data,
    pageSize,
    totalElements,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasPreviousPage,
    hasNextPage,
    setSorting,
    sortBy,
    sortOrder,
    isSearching,
  } = usePersistedBoxArchivedMembers()

  const columns = [
    columnHelper.accessor("username", {
      header: () => (
        <HeaderTemplate
          title={word("box.archived.table.member.name")}
          variant={
            sortBy === MemberColumnToSort.NAME
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(MemberColumnToSort.NAME, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(MemberColumnToSort.NAME, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => (
        <UserCell
          member={{
            avatar: member.avatar,
            firstname: member.firstname,
            lastname: member.lastname,
          }}
          subtitle={member.getUserName()}
          subtitleColor="neutral"
        />
      ),
    }),
    columnHelper.accessor("lastMembership", {
      header: () => (
        <HeaderTemplate
          title={word("box.archived.table.member.lastOffer")}
          variant={
            sortBy === MemberColumnToSort.LAST_MEMBERSHIP
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(MemberColumnToSort.LAST_MEMBERSHIP, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(MemberColumnToSort.LAST_MEMBERSHIP, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {member.lastMembership ?? "-"}
        </Text>
      ),
    }),
    columnHelper.accessor("inscriptionDate", {
      header: () => (
        <HeaderTemplate
          title={word("box.archived.table.member.inscriptionDate")}
          variant={
            sortBy === MemberColumnToSort.INSCRIPTION_DATE
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(MemberColumnToSort.INSCRIPTION_DATE, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(MemberColumnToSort.INSCRIPTION_DATE, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {member.inscriptionDate ? dayjs(member.inscriptionDate).format("L") : "-"}
        </Text>
      ),
    }),
    columnHelper.accessor("archivedDate", {
      header: () => (
        <HeaderTemplate
          title={word("box.archived.table.member.archivedDate")}
          variant={
            sortBy === MemberColumnToSort.ARCHIVED_DATE
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(MemberColumnToSort.ARCHIVED_DATE, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(MemberColumnToSort.ARCHIVED_DATE, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: member } }) => (
        <Text fontStyle="BM" className="text-textDefault-body">
          {member.archivedDate ? dayjs(member.archivedDate).format("L") : "-"}
        </Text>
      ),
    }),
  ]

  const onSelectedArchivedMember = (e: MemberItemEntity) => {
    navigateToMemberDetailsInfo(e.id, {
      onHide: () => {
        setSelectedArchivedMember(undefined)
        return Promise.resolve(true)
      },
      replaceAllComponents: true,
    })
    setSelectedArchivedMember(e)
  }

  if (data.length === 0)
    return (
      <EmptyView
        className="w-full"
        image={{ src: ChatEmpty }}
        title={isSearching ? word("searching.list.empty.title") : word("archived.list.empty.title")}
        description={isSearching ? word("searching.list.empty.description") : word("archived.list.empty.description")}
        paddingType="In_table"
      />
    )

  return (
    <div className={cn("flex w-full flex-col px-6", className)} {...props}>
      <HUDatatable
        data={data}
        columns={columns}
        pageSize={pageSize}
        setPageSize={setPageSize}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handleRowSelectionChange={(row: MemberItemEntity) => onSelectedArchivedMember(row)}
        selectedRowIndex={data.findIndex((item) => item.id === selectedArchivedMember?.id)}
        totalElements={totalElements}
      />
    </div>
  )
}
